<script setup lang="ts">
import { $fileTypeIcon } from "@/vf"
import { computed, ref } from "vue"

const props = withDefaults(
    defineProps<{
        accept?: string
        modelValue?: File[]
        /** Allow selecting more than one file. Ignored when maxFiles is set. */
        multiple?: boolean
        /** Maximum number of files that can be added. Takes precedence over `multiple`. */
        maxFiles?: number
    }>(),
    {
        modelValue: () => [],
        accept: "*/*",
        multiple: true,
    },
)

const emit = defineEmits<{
    (e: "change", value: File[]): void[]
    (e: "update:modelValue", value: File[]): void[]
}>()

const file = ref<HTMLInputElement>(null!)

const isDraggingOver = ref(false)

const maxFileCount = computed(() => props.maxFiles ?? (props.multiple ? Infinity : 1))
const canAddMore = computed(() => props.modelValue.length < maxFileCount.value)

function isImage(file: File): boolean {
    return file.type.startsWith("image/")
}

function previewUrl(file: File): string {
    return URL.createObjectURL(file)
}

function update(files: File[]) {
    emit("change", files)
    emit("update:modelValue", files)
}

function addFiles(newFiles: FileList) {
    const files = [...props.modelValue, ...Array.from(newFiles)].slice(0, maxFileCount.value)
    update(files)
}

function onChange() {
    if (file.value.files?.length) {
        addFiles(file.value.files)
    }
    file.value.value = ""
}

function remove(target: File) {
    update(props.modelValue.filter(f => f !== target))
}

function dragover(event: DragEvent) {
    event.preventDefault()
    if (canAddMore.value) {
        // Add some visual fluff to show the user can drop its files
        isDraggingOver.value = true
    }
}

function dragleave() {
    // Clean up
    isDraggingOver.value = false
}

function drop(event: DragEvent) {
    event.preventDefault()
    isDraggingOver.value = false

    if (!canAddMore.value || !event.dataTransfer?.files) {
        return
    }

    addFiles(event.dataTransfer.files)
}
</script>

<template>
    <div class="droparea" :class="{ dragover: isDraggingOver }" @dragover="dragover" @dragleave="dragleave" @drop="drop">
        <label class="d-block text-center mb-4 py-5 border border-light" for="assetsFieldHandle" v-if="canAddMore">
            <i class="fa fa-fw fa-upload"></i>
            {{ $t("@app:components.file_drop_area") }}

            <input
                type="file"
                :multiple="maxFileCount > 1"
                id="assetsFieldHandle"
                @change="onChange"
                @click.stop
                ref="file"
                :accept="props.accept"
                class="d-none"
            />
        </label>

        <div class="droparea__list" v-if="modelValue.length">
            <div class="droparea__item" v-for="item in modelValue" :key="`${item.name}${item.size}`">
                <img v-if="isImage(item)" :src="previewUrl(item)" class="droparea__thumb" alt="" />
                <i v-else :class="'fa-fw fs-1-5 ' + $fileTypeIcon(item.type)"></i>

                <div class="flex-grow-1 text-truncate">{{ item.name }}</div>

                <button
                    type="button"
                    class="btn btn-link p-2 m-0"
                    :title="$t('crud:button.delete')"
                    @click.prevent="remove(item)"
                >
                    <i class="fa fa-fw fa-trash"></i>
                </button>
            </div>
        </div>
    </div>
</template>

<style scoped>
.droparea {
    border: 2px solid transparent;
    width: 100%;
}
.droparea.dragover {
    background-color: #f0f0f0;
    border: 2px dashed #ccc;
}
.droparea__list {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
}
.droparea__item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.25rem;
}
.droparea__thumb {
    width: 2.5rem;
    height: 2.5rem;
    object-fit: cover;
    border-radius: 4px;
    flex-shrink: 0;
}
</style>
